import {mergeAttributes, Node} from "@tiptap/core";
import {ReactNodeViewRenderer} from "@tiptap/react";
import PrintoutWritingAreaNodeView from "./PrintoutWritingAreaNodeView";
import {
    clampWritingAreaHeightMm,
    DEFAULT_WRITING_AREA_ATTRS,
    getWritingAreaAttrs,
} from "./printoutWritingAreaOptions";
import type {PrintoutWritingAreaAttrs} from "./printoutWritingAreaOptions";

export const PrintoutWritingAreaNode = Node.create({
    name: "printoutWritingArea",

    group: "block",
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
        return {
            heightMm: {
                default: DEFAULT_WRITING_AREA_ATTRS.heightMm,
                parseHTML: (element: HTMLElement) => clampWritingAreaHeightMm(
                    element.getAttribute("data-printout-writing-area-height-mm"),
                ),
                renderHTML: (attributes: Record<string, unknown>) => ({
                    "data-printout-writing-area-height-mm": clampWritingAreaHeightMm(attributes.heightMm),
                }),
            },
            label: {
                default: DEFAULT_WRITING_AREA_ATTRS.label,
                parseHTML: (element: HTMLElement) => (
                    element.getAttribute("data-printout-writing-area-label") ?? DEFAULT_WRITING_AREA_ATTRS.label
                ),
                renderHTML: (attributes: Record<string, unknown>) => ({
                    "data-printout-writing-area-label": typeof attributes.label === "string"
                        ? attributes.label
                        : DEFAULT_WRITING_AREA_ATTRS.label,
                }),
            },
            style: {
                default: DEFAULT_WRITING_AREA_ATTRS.style,
                parseHTML: (element: HTMLElement) => element.getAttribute("data-printout-writing-area-style"),
                renderHTML: (attributes: Record<string, unknown>) => ({
                    "data-printout-writing-area-style": attributes.style,
                }),
            },
        };
    },

    parseHTML() {
        return [{tag: 'div[data-type="printout-writing-area"]'}];
    },

    renderHTML({HTMLAttributes, node}) {
        const attrs = getWritingAreaAttrs(node.attrs as PrintoutWritingAreaAttrs);
        const children: (string | Record<string, string>)[][] = [];

        if (attrs.label) {
            children.push([
                "div",
                {class: "printout-rendered-writing-area-label"},
                attrs.label,
            ]);
        }

        children.push([
            "div",
            {class: "printout-rendered-writing-area-surface"},
        ]);

        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-writing-area",
                "data-printout-writing-area-style": attrs.style,
                class: "printout-rendered-writing-area",
                style: `--printout-writing-area-height:${attrs.heightMm}mm;width:100%`,
            }),
            ...children,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutWritingAreaNodeView, {
            trackNodeViewPosition: true,
        });
    },
});
